// React Query mutation hooks for Notice Management (PRD 13 §3.2–§3.4).
// Create / update carry the publish intent via `status` (PUBLISHED | DRAFT) —
// there is no separate publish endpoint. Delete is Root-only (gated in the UI;
// BE enforces). Errors propagate to the caller for toast handling.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { noticesApi } from '../api/notices.adapter';
import type {
  CreateNoticeInput,
  Notice,
  UpdateNoticeInput,
} from '../types/notices.types';
import { noticeQueryKeys } from './use-notices';

export function useCreateNotice() {
  const qc = useQueryClient();
  return useMutation<Notice, Error, CreateNoticeInput>({
    mutationFn: (input) => noticesApi.create(input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: noticeQueryKeys.list() });
      // Seed the detail cache so an immediate re-open skips the spinner.
      qc.setQueryData(noticeQueryKeys.detail(created.id), created);
    },
  });
}

export function useUpdateNotice() {
  const qc = useQueryClient();
  return useMutation<Notice, Error, { id: number; input: UpdateNoticeInput }>({
    mutationFn: ({ id, input }) => noticesApi.update(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: noticeQueryKeys.list() });
      qc.setQueryData(noticeQueryKeys.detail(updated.id), updated);
    },
  });
}

export function useDeleteNotice() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => noticesApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: noticeQueryKeys.list() });
      qc.removeQueries({ queryKey: noticeQueryKeys.detail(id) });
    },
  });
}
